import axios from "axios";
import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CheckCircle, X } from "lucide-react";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const toNumericId = (id) => {
    const numericId = parseInt(id, 10);
    if (isNaN(numericId) || numericId <= 0 || id === undefined || id === null) {
      throw new Error(`Invalid or missing user ID: ${id}`);
    }
    return numericId;
  };

  const setAuthHeaders = (token) => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("token", token);
    } else {
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem("token");
    }
  };

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          setAuthHeaders(token);
          const response = await axios.get("https://backend.emerald.azadnoori.com/api/v1/users/me", {
            withCredentials: true,
          });
          const userData = response.data.data.user;
          if (!userData?.id) {
            throw new Error("User ID missing in response");
          }
          const validatedUser = { ...userData, id: toNumericId(userData.id) };
          setUser(validatedUser);
        } catch (error) {
          setUser(null);
          setToken(null);
          setAuthHeaders(null);
          localStorage.removeItem("token");
          toast.error("Session expired. Please log in again.", {
            position: "top-right",
            icon: <X size={20} className="text-red-600" />,
          });
          navigate("/login", { replace: true });
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token, navigate]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        "https://backend.emerald.azadnoori.com/api/v1/users/login",
        { email, password },
        { withCredentials: true }
      );
  
      const userData = response.data.data?.user;
      const token = response.data.token;
      if (!userData || !userData.id || !token) {
        throw new Error(
          `Invalid login response: ${JSON.stringify({
            userId: userData?.id,
            token: !!token,
          })}`
        );
      }
  
      const numericId = toNumericId(userData.id);
      const validatedUser = { ...userData, id: numericId };
      setUser(validatedUser);
      setToken(token);
      setAuthHeaders(token);
  

      toast.success("Successfully logged in!", {
        position: "top-right",
        autoClose: 3000,
        icon: <CheckCircle size={20} className="text-emerald-600" />,
      });
  
      // Validate role
      const role = userData.role;
      if (!["Admin", "user", "Super Admin"].includes(role)) {
        navigate(`/user-dashboard/${numericId}`, { replace: true });
        return;
      }
  
      // Redirect based on role
      const redirectPath = role === "Admin" || role === "Super Admin"
        ? "/dashboard/admin"
        : `/user-dashboard/${numericId}`;
      navigate(redirectPath, { replace: true });
    } catch (error) {
      let errorMessage = "Failed to log in. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = "Invalid email or password.";
        } else if (error.response.status === 404) {
          errorMessage = "User not found.";
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      }
      toast.error(errorMessage, {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
      throw error;
    }
  };
  const logout = async () => {
    try {
      await axios.post(
        "https://backend.emerald.azadnoori.com/api/v1/users/logout",
        {},
        { withCredentials: true }
      );
      toast.success("Successfully logged out!", {
        position: "top-right",
        autoClose: 3000,
        icon: <CheckCircle size={20} className="text-emerald-600" />,
      });
    } catch (error) {
      toast.error("Failed to log out. Please try again.", {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
    } finally {
      setUser(null);
      setToken(null);
      setAuthHeaders(null);
      navigate("/login", { replace: true });
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);